import { AvState } from "@minervaproject/avsubsystem/state";
import { configureStore } from "@reduxjs/toolkit";
// eslint-disable-next-line no-restricted-imports
import { type TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

import { createPubsubSlice } from "./pubsub-slice";
import { AIStore } from "./slices/ai";
import { AttendanceStore } from "./slices/attendance";
import { AuthStore } from "./slices/auth";
import { ChatMentionsStore } from "./slices/chat-mentions";
import { ComputedStore } from "./slices/computed";
import { LocalStore } from "./slices/local";
import { MonitoringStore } from "./slices/monitoring";
import { PubsubVirtualClassStateSlice } from "./slices/pubsub-virtual-class-state";
import { ViewStore } from "./slices/view";
import { setStoreGetter } from "./store-log";

import type {
  ActivityListFilterType,
  AssessmentListFilterType,
  BreakoutGroupListFilterType,
  ChatMessageListFilterType,
  ClassSessionListFilterType,
  ClassSessionSummaryListFilterType,
  ClassSessionUserSummaryListFilterType,
  ClassUserListFilterType,
  OrgUserListFilterType,
  OutcomeAssessmentListFilterType,
  OutcomeListFilterType,
  OutcomeSubgroupListFilterType,
  PageAccessDataListFilterType,
  PageGroupListFilterType,
  PageGroupUserListFilterType,
  PageListFilterType,
  PageOutcomeListFilterType,
  PublicationListFilterType,
  RecordingSessionListFilterType,
  SectionOutcomeListFilterType,
  SectionUserListFilterType,
  SubmissionListFilterType,
  TopicListFilterType,
} from "components/server-filters";
import type {
  ActivityType,
  AssessmentType,
  BreakoutGroupType,
  BulkImportJobType,
  ChatMessageType,
  ClassSessionSummaryType,
  ClassSessionType,
  ClassSessionUserSummaryType,
  ClassUserType,
  CourseGroupingExtendedInsertType,
  CourseGroupingType,
  CourseGroupingUserType,
  CourseType,
  CourseUserType,
  FullAssessmentType,
  OrgType,
  OrgUserType,
  OutcomeAssessmentType,
  OutcomeGroupType,
  OutcomeSubgroupType,
  OutcomeType,
  PageAccessDataType,
  PageExtendedInsertType,
  PageGroupType,
  PageGroupUserType,
  PageOutcomeType,
  PageType,
  PublicationType,
  RecordingSessionType,
  SectionOutcomeType,
  SectionType,
  SectionUserType,
  SubmissionType,
  TermType,
  TopicExtendedInsertType,
  TopicType,
  UserType,
} from "components/server-types";

// Pubsub Model Slices - Keep in sync with backend viewsets
export const slices = {
  //------------------------------------------------------------
  // Django app: chat

  chat_messages: createPubsubSlice<ChatMessageType, ChatMessageListFilterType>(
    "chat_messages",
    "chat/messages",
  ),

  //------------------------------------------------------------
  // Django app: custom_auth

  users: createPubsubSlice<UserType>("users", "auth/users"),

  //------------------------------------------------------------
  // Django app: material

  activities: createPubsubSlice<ActivityType, ActivityListFilterType>("activities"),
  assessments: createPubsubSlice<AssessmentType, AssessmentListFilterType, FullAssessmentType>(
    "assessments",
  ),
  breakout_groups: createPubsubSlice<BreakoutGroupType, BreakoutGroupListFilterType>(
    "breakout_groups",
  ),
  bulk_import_jobs: createPubsubSlice<BulkImportJobType>("bulk_import_jobs"),
  class_sessions: createPubsubSlice<ClassSessionType, ClassSessionListFilterType>("class_sessions"),
  class_session_summaries: createPubsubSlice<
    ClassSessionSummaryType,
    ClassSessionSummaryListFilterType
  >("class_session_summaries"),
  class_session_user_summaries: createPubsubSlice<
    ClassSessionUserSummaryType,
    ClassSessionUserSummaryListFilterType
  >("class_session_user_summaries"),
  class_users: createPubsubSlice<ClassUserType, ClassUserListFilterType>("class_users"),
  course_groupings: createPubsubSlice<
    CourseGroupingType,
    CourseGroupingType,
    CourseGroupingExtendedInsertType
  >("course_groupings"),
  course_grouping_users: createPubsubSlice<CourseGroupingUserType>("course_grouping_users"),
  course_users: createPubsubSlice<CourseUserType>("course_users"),
  courses: createPubsubSlice<CourseType>("courses"),
  section_outcomes: createPubsubSlice<SectionOutcomeType, SectionOutcomeListFilterType>(
    "section_outcomes",
  ),
  section_users: createPubsubSlice<SectionUserType, SectionUserListFilterType>("section_users"),
  sections: createPubsubSlice<SectionType>("sections"),
  outcome_assessments: createPubsubSlice<OutcomeAssessmentType, OutcomeAssessmentListFilterType>(
    "outcome_assessments",
  ),
  outcome_groups: createPubsubSlice<OutcomeGroupType>("outcome_groups"),
  outcome_subgroups: createPubsubSlice<OutcomeSubgroupType, OutcomeSubgroupListFilterType>(
    "outcome_subgroups",
  ),
  outcomes: createPubsubSlice<OutcomeType, OutcomeListFilterType>("outcomes"),
  page_access_data: createPubsubSlice<PageAccessDataType, PageAccessDataListFilterType>(
    "page_access_data",
  ),
  page_group_users: createPubsubSlice<PageGroupUserType, PageGroupUserListFilterType>(
    "page_group_users",
  ),
  page_groups: createPubsubSlice<PageGroupType, PageGroupListFilterType>("page_groups"),
  page_outcomes: createPubsubSlice<PageOutcomeType, PageOutcomeListFilterType>("page_outcomes"),
  pages: createPubsubSlice<PageType, PageListFilterType, PageExtendedInsertType>("pages"),
  submissions: createPubsubSlice<SubmissionType, SubmissionListFilterType>("submissions"),
  terms: createPubsubSlice<TermType>("terms"),
  topics: createPubsubSlice<TopicType, TopicListFilterType, TopicExtendedInsertType>("topics"),

  //------------------------------------------------------------
  // Django app: orgs

  orgs: createPubsubSlice<OrgType>("orgs", "orgs/orgs"),
  org_users: createPubsubSlice<OrgUserType, OrgUserListFilterType>("org_users", "orgs/org_users"),

  //------------------------------------------------------------
  // Django app: publishing

  publications: createPubsubSlice<PublicationType, PublicationListFilterType>(
    "publications",
    "publishing/publications",
  ),

  //------------------------------------------------------------
  // Django app: recording

  recording_sessions: createPubsubSlice<RecordingSessionType, RecordingSessionListFilterType>(
    "recording_sessions",
    "recording/recording_sessions",
  ),
};

export const createStore = (initialState?: unknown) =>
  configureStore({
    reducer: {
      ai: AIStore.reducer,
      av: AvState.avSliceReducer,
      auth: AuthStore.reducer,
      chatMentions: ChatMentionsStore.reducer,
      view: ViewStore.reducer,
      local: LocalStore.reducer,
      attendance: AttendanceStore.reducer,
      monitoring: MonitoringStore.reducer,
      computed: ComputedStore.reducer,
      pubsub_virtual_class_state: PubsubVirtualClassStateSlice.reducer,

      /// Pubsub Model Slices - Keep in sync with backend models ///
      activities: slices.activities.slice.reducer,
      assessments: slices.assessments.slice.reducer,
      breakout_groups: slices.breakout_groups.slice.reducer,
      bulk_import_jobs: slices.bulk_import_jobs.slice.reducer,
      chat_messages: slices.chat_messages.slice.reducer,
      class_sessions: slices.class_sessions.slice.reducer,
      class_session_summaries: slices.class_session_summaries.slice.reducer,
      class_session_user_summaries: slices.class_session_user_summaries.slice.reducer,
      class_users: slices.class_users.slice.reducer,
      courses: slices.courses.slice.reducer,
      course_groupings: slices.course_groupings.slice.reducer,
      course_grouping_users: slices.course_grouping_users.slice.reducer,
      course_users: slices.course_users.slice.reducer,
      section_outcomes: slices.section_outcomes.slice.reducer,
      section_users: slices.section_users.slice.reducer,
      sections: slices.sections.slice.reducer,
      org_users: slices.org_users.slice.reducer,
      orgs: slices.orgs.slice.reducer,
      outcome_assessments: slices.outcome_assessments.slice.reducer,
      outcome_groups: slices.outcome_groups.slice.reducer,
      outcome_subgroups: slices.outcome_subgroups.slice.reducer,
      outcomes: slices.outcomes.slice.reducer,
      page_access_data: slices.page_access_data.slice.reducer,
      page_group_users: slices.page_group_users.slice.reducer,
      page_groups: slices.page_groups.slice.reducer,
      page_outcomes: slices.page_outcomes.slice.reducer,
      pages: slices.pages.slice.reducer,
      publications: slices.publications.slice.reducer,
      recording_sessions: slices.recording_sessions.slice.reducer,
      submissions: slices.submissions.slice.reducer,
      terms: slices.terms.slice.reducer,
      topics: slices.topics.slice.reducer,
      users: slices.users.slice.reducer,
    },
    preloadedState: initialState,
  });

export const store = createStore();

// Provide a getter for store state to the logging module without creating a circular dependency
setStoreGetter(() => store.getState());

/// Pubsub Model APIs - Keep in sync with backend models ///
Object.values(slices).forEach((slice) => slice.addListener(store.dispatch));

export const storeApi = {
  activities: slices.activities.getApi(store.dispatch),
  assessments: slices.assessments.getApi(store.dispatch),
  breakout_groups: slices.breakout_groups.getApi(store.dispatch),
  bulk_import_jobs: slices.bulk_import_jobs.getApi(store.dispatch),
  chat_messages: slices.chat_messages.getApi(store.dispatch),
  class_sessions: slices.class_sessions.getApi(store.dispatch),
  class_session_summaries: slices.class_session_summaries.getApi(store.dispatch),
  class_session_user_summaries: slices.class_session_user_summaries.getApi(store.dispatch),
  class_users: slices.class_users.getApi(store.dispatch),
  courses: slices.courses.getApi(store.dispatch),
  course_groupings: slices.course_groupings.getApi(store.dispatch),
  course_grouping_users: slices.course_grouping_users.getApi(store.dispatch),
  course_users: slices.course_users.getApi(store.dispatch),
  section_outcomes: slices.section_outcomes.getApi(store.dispatch),
  section_users: slices.section_users.getApi(store.dispatch),
  sections: slices.sections.getApi(store.dispatch),
  org_users: slices.org_users.getApi(store.dispatch),
  orgs: slices.orgs.getApi(store.dispatch),
  outcome_assessments: slices.outcome_assessments.getApi(store.dispatch),
  outcome_groups: slices.outcome_groups.getApi(store.dispatch),
  outcome_subgroups: slices.outcome_subgroups.getApi(store.dispatch),
  outcomes: slices.outcomes.getApi(store.dispatch),
  page_access_data: slices.page_access_data.getApi(store.dispatch),
  page_group_users: slices.page_group_users.getApi(store.dispatch),
  page_groups: slices.page_groups.getApi(store.dispatch),
  page_outcomes: slices.page_outcomes.getApi(store.dispatch),
  pages: slices.pages.getApi(store.dispatch),
  publications: slices.publications.getApi(store.dispatch),
  recording_sessions: slices.recording_sessions.getApi(store.dispatch),
  submissions: slices.submissions.getApi(store.dispatch),
  terms: slices.terms.getApi(store.dispatch),
  topics: slices.topics.getApi(store.dispatch),
  users: slices.users.getApi(store.dispatch),
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout the app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
// (window as any).replaceAll = (state) => store.dispatch(replaceAllAction(state));
